<template>
  <v-layout row justify-center>
    <v-dialog fullscreen transition="bottom-sheet-transition" :value="providerId !== null && showDialog">
      <v-card>
        <modal-toolbar
          :title="editedProvider.name"
          :disable-save="disableSave"
          @keydown.esc.native="closeModal"
          @close="closeModal"
          @save="saveModal"
        />
        <v-subheader> <span v-t="'editingContainer.providerSettings'" /> </v-subheader>
        <v-card-text style="padding-top:0">
          <provider-settings ref="providerSettings" :provider="editedProvider" :active-providers-data="activeProvidersData" :loading-provider="loadingProvider" />
        </v-card-text>
        <v-subheader>
          <span v-t="'editingContainer.categories'" />
          <category-add-button @addCategory="addCategory" />
        </v-subheader>
        <v-card-text style="padding:0">
          <dish-categories ref="dishCategories" :categories="editedProvider.categories" @deleteCategory="deleteCategory" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import gql from 'graphql-tag';
import ErrorHandler from '@/services/ErrorHandler';
import ValidationService from '@/services/ValidationService';
import NotificationService from '@/services/NotificationService';
import DishCategoriesList from './dish-categories/DishCategoriesList.vue';
import ModalToolbar from './modal-toolbar/ModalToolbar.vue';
import ProviderSettings from './provider-settings/ProviderSettings.vue';
import CategoryAddButton from './category-add-button/CategoryAddButton.vue';

const getInitData = () => ({
  invalidFields: [],
  modalDirty: false,
});

export default {
  name: 'ProviderEditModal',
  components: {
    'dish-categories': DishCategoriesList,
    'modal-toolbar': ModalToolbar,
    'provider-settings': ProviderSettings,
    'category-add-button': CategoryAddButton,
  },
  props: {
    providerId: {
      type: Number,
      required: true,
    },
    isFreeOfCharge: Boolean,
    copyingProvider: Boolean,
    disableSave: Boolean,
    selectedLanguage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return Object.assign({}, getInitData(), {
      editedProvider: {
        categories: [{
          active: true,
          name: this.selectedLanguage === 1 ? 'Sriubos' : 'Soups',
          translation: this.selectedLanguage === 1 ? 'Soups' : 'Sriubos',
          selections: [{
            dishes: [{
              name: '',
            }],
            price: null,
          }],
          soups: true,
        }, {
          name: this.selectedLanguage === 1 ? 'Pagrindiniai Patiekalai' : 'Main Dishes',
          translation: this.selectedLanguage === 1 ? 'Main Dishes' : 'Pagrindiniai Patiekalai',
          selections: [{
            dishes: [{
              name: '',
            }],
            price: null,
          }],
          soups: false,
        }],
        name: '',
        isFake: true,
      },
      showDialog: false,
      activeProvidersData: [],
      loadingProvider: 0,
    });
  },
  computed: {
    invalidFieldsFormatted() {
      return `Invalid Fields: ${[...new Set(this.invalidFields)].join(', ')}.`;
    },
  },
  mounted() {
    document.getElementsByTagName('html')[0].style.overflow = 'hidden';

    if (this.providerId === -1) {
      this.showDialog = true;
    }
    window.addEventListener('keyup', this.handleEsc);
    const unwatch = this.$watch('editedProvider', (newV, oldV) => {
      if (oldV.isFake && this.providerId > 0) {
        return;
      }
      this.modalDirty = true;
      unwatch();
    }, { deep: true });
  },
  destroyed() {
    document.getElementsByTagName('html')[0].style.overflow = '';
    window.removeEventListener('keyup', this.handleEsc);
  },
  methods: {
    saveModal() {
      const categoriesValid = this.$refs.dishCategories.validate();
      const providersValid = this.$refs.providerSettings.validate();
      const selectionsEmpty = this.editedProvider.categories.filter(c => c.selections.length === 0).map(c => c.name);
      if (categoriesValid && providersValid && selectionsEmpty.length === 0) {
        this.$emit('save', this.editedProvider);
      } else if (selectionsEmpty.length > 0) {
        NotificationService.showAlert({
          message: this.$t('editingContainer.emptyCategories', { categories: selectionsEmpty.join(', ') }),
          level: 'error',
        });
      } else {
        // SetTimeout is required since after validation it needs a tick to apply data-error attribute
        setTimeout(() => {
          const invalidFieldNames = [...new Set(ValidationService.getInvalidFieldNames())];
          NotificationService.showAlert({
            message: this.$t('editingContainer.invalidFields', { fields: invalidFieldNames.join(', ') }),
            level: 'error',
          });
        });
      }
    },
    closeModal() {
      if (this.modalDirty) {
        NotificationService.showUnsavedChangesModal({
          header: 'common.discardChanges',
          body: 'common.discardChangesBody',
          agreeCallback: () => this.$emit('close'),
        });
      } else {
        this.showDialog = false;
        this.$emit('close');
      }
    },
    addCategory(category) {
      this.editedProvider.categories.push({
        name: category.name,
        translation: category.translation,
        soups: category.soups,
        active: true,
        selections: [{
          dishes: [{
            name: '',
          }],
          price: null,
        }],
      });
    },
    deleteCategory(deletedCategory) {
      this.editedProvider.categories = this.editedProvider.categories.filter(c => c !== deletedCategory);
    },
    providerSettingsValid() {
      return this.$refs.providerSettings.settingsValid();
    },
    handleEsc({ code }) {
      if (code === 'Escape') {
        this.closeModal();
      }
    },
  },
  apollo: {
    $loadingKey: 'loadingProvider',
    provider: {
      fetchPolicy: 'network-only',
      query() {
        const providersQuery = `provider(providerId: $providerId) {
            id
            name
            color
            providerDataId
            categories {
              id
              translation: title(languageId: ${this.selectedLanguage === 1 ? 2 : 1}, fallbackToDefault: $fallbackToDefault)
              name: title(languageId: ${this.selectedLanguage}),
              selections {
                id
                price
                maxCount
                requestsCount
                dishes {
                  id
                  translation: title(languageId: ${this.selectedLanguage === 1 ? 2 : 1}, fallbackToDefault: $fallbackToDefault)
                  name: title(languageId: ${this.selectedLanguage}),
                  isSoup
                }
              }
            }
          }`;

        if (this.providerId > 0) {
          return gql`query ProviderEditingQuery($providerId: Int!, $fallbackToDefault: Boolean) {
              activeProvidersData {
                id
                name
              }
              ${providersQuery}
            }`;
        }

        return gql`query ActiveProviderNamesQuery {
            activeProvidersData {
              id
              name
            }
          }`;
      },
      variables() {
        return {
          providerId: this.providerId,
          fallbackToDefault: false,
        };
      },
      error(error) {
        this.provider = {};
        if (!this.loadingProvider) {
          ErrorHandler.handleApolloError(error);
        }
      },
      result({ data, loading }) {
        if (!loading) {
          NotificationService.clearNetworkNotification();
        }
        this.showDialog = true;
        this.activeProvidersData = data.activeProvidersData;
        if (data.provider) {
          const { provider } = data;
          this.editedProvider = {
            id: provider.id,
            name: provider.name,
            color: provider.color,
            providerDataId: provider.providerDataId,
            categories: provider.categories.map(c => ({
              id: c.id,
              name: c.name,
              translation: c.translation,
              soups: c.selections.every(s => s.dishes[0].isSoup),
              selections: c.selections.map(s => ({
                id: s.id,
                price: s.price,
                maxCount: s.maxCount,
                requestsCount: this.copyingProvider ? 0 : s.requestsCount,
                dishes: s.dishes.map(d => ({
                  id: d.id,
                  name: d.name,
                  translation: d.translation,
                  isSoup: d.isSoup,
                })),
              })),
            })),
          };
        }

        if (this.editedProvider.categories.length > 0) {
          this.editedProvider.categories[0].active = true;
        }
      },
      update(data) {
        return data;
      },
    },
  },
};
</script>
